import { Plan } from '../types';

interface Props {
  plan: Plan;
  usageCount: number;
  isSelected: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

const fmtBRL = (v: number) =>
  v === 0 ? 'Grátis' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtLimit = (v: number) => (v === -1 ? '∞' : String(v));

const PlanCard = ({ plan, usageCount, isSelected, onEdit, onDuplicate, onToggleActive, onDelete }: Props) => {
  const annualSaving =
    plan.price_monthly > 0 && plan.price_annual > 0
      ? plan.price_monthly * 12 - plan.price_annual
      : 0;

  const badges = [
    plan.is_featured && { label: 'Destaque', color: 'bg-violet-600 text-white' },
    plan.is_recommended && { label: 'Recomendado', color: 'bg-emerald-600/20 text-emerald-400' },
    plan.is_best_value && { label: 'Melhor custo', color: 'bg-amber-500/20 text-amber-400' },
    plan.is_legacy && { label: 'Legado', color: 'bg-white/10 text-white/40' },
    plan.is_internal && { label: 'Interno', color: 'bg-cyan-500/20 text-cyan-400' },
  ].filter(Boolean) as { label: string; color: string }[];

  return (
    <div
      className={`bg-white/5 border rounded-2xl p-5 transition-all ${
        isSelected ? 'border-violet-500 ring-2 ring-violet-500/20' : 'border-white/10 hover:border-white/20'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${plan.is_active ? 'bg-violet-600/20' : 'bg-white/5'}`}>
            <i className={`ri-price-tag-3-line text-[16px] ${plan.is_active ? 'text-violet-400' : 'text-white/30'}`} />
          </div>
          <div>
            <h3 className="text-[15px] font-700 text-white leading-tight">{plan.name}</h3>
            <span className="text-[11px] text-white/40 font-mono">{plan.slug}</span>
          </div>
        </div>
        <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${plan.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
          {plan.is_active ? 'Ativo' : 'Inativo'}
        </span>
      </div>

      {/* Badges */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {badges.map((b) => (
            <span key={b.label} className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${b.color}`}>{b.label}</span>
          ))}
        </div>
      )}

      {/* Pricing */}
      <div className="mb-3">
        {plan.price_monthly > 0 ? (
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <span className="text-[10px] text-white/30 block mb-0.5">Mensal</span>
              <span className="text-[18px] font-700 text-white">{fmtBRL(plan.price_monthly)}</span>
              <span className="text-[11px] text-white/40">/mês</span>
            </div>
            {plan.price_annual > 0 && (
              <div>
                <span className="text-[10px] text-white/30 block mb-0.5">Anual</span>
                <span className="text-[18px] font-700 text-emerald-400">{fmtBRL(plan.price_annual)}</span>
                <span className="text-[11px] text-white/40">/ano</span>
                {plan.annual_discount_percent > 0 && (
                  <span className="ml-1.5 text-[10px] font-600 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">-{plan.annual_discount_percent}%</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[22px] font-700 text-white">{fmtBRL(plan.price_brl)}</span>
            {plan.price_brl > 0 && (
              <span className="text-[12px] text-white/40">
                /{plan.periodicity === 'monthly' ? 'mês' : plan.periodicity === 'annual' ? 'ano' : 'vitalício'}
              </span>
            )}
          </div>
        )}
        {annualSaving > 0 && (
          <p className="text-[11px] text-emerald-400 mt-1">Economia anual: {fmtBRL(annualSaving)}</p>
        )}
      </div>

      {/* Limits grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-3">
        {[
          { icon: 'ri-bank-line', label: 'Contas', val: fmtLimit(plan.accounts_limit) },
          { icon: 'ri-bank-card-line', label: 'Cartões', val: fmtLimit(plan.cards_limit) },
          { icon: 'ri-swap-line', label: 'Lançamentos/mês', val: fmtLimit(plan.monthly_transactions_limit) },
          { icon: 'ri-flag-line', label: 'Metas', val: fmtLimit(plan.goals_limit) },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5 text-[11px]">
            <i className={`${item.icon} text-white/30`} />
            <span className="text-white/50">{item.label}:</span>
            <span className="text-white font-600">{item.val}</span>
          </div>
        ))}
      </div>

      {/* Features chips */}
      <div className="flex flex-wrap gap-1 mb-4">
        {plan.reports_enabled && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-white/50 rounded">Relatórios</span>}
        {plan.can_export_csv && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-white/50 rounded">CSV</span>}
        {plan.can_export_pdf && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-white/50 rounded">PDF</span>}
        {plan.can_access_advanced_insights && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-white/50 rounded">Insights</span>}
        {plan.can_access_priority_support && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-white/50 rounded">Suporte prioritário</span>}
        {plan.trial_available && (
          <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded">Trial {plan.trial_days}d</span>
        )}
      </div>

      {/* Meta */}
      <div className="text-[11px] text-white/30 mb-3">
        {usageCount} usuário(s) · Ordem {plan.display_order}
        {!plan.is_public && <span className="ml-2 text-orange-400">Oculto</span>}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-3 border-t border-white/10">
        <button
          onClick={onEdit}
          className="flex-1 py-2 rounded-xl bg-violet-600/10 hover:bg-violet-600/20 text-violet-400 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
        >
          <i className="ri-edit-line" />Editar
        </button>
        <button
          onClick={onDuplicate}
          className="flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
        >
          <i className="ri-file-copy-line" />Duplicar
        </button>
        <button
          onClick={onToggleActive}
          className={`flex-1 py-2 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
            plan.is_active ? 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-400' : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400'
          }`}
        >
          <i className={plan.is_active ? 'ri-pause-line' : 'ri-play-line'} />
          {plan.is_active ? 'Pausar' : 'Ativar'}
        </button>
        {usageCount === 0 && (
          <button
            onClick={onDelete}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
